/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Component, OnInit, ChangeDetectionStrategy, OnDestroy } from '@angular/core';
import { PlanState } from 'src/app/services/plan-state.service';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import {
	CONSTRAINT_ACCEPTANCE_PANEL_CUSTOMIZE_LABEL,
	CONSTRAINT_ACCEPTANCE_PANEL_JUMP_TO_PLAN_LABEL,
	CONSTRAINT_ACCEPTANCE_PANEL_CLOSE_EDIT,
} from '@touchplan/strings';
import { CONSTRAINT_ACCEPTANCE_STATE, Plan, Ticket } from '@touchplan/common';
import { trigger, transition, animate, style } from '@angular/animations';
import { TicketEditCommonDesktopService } from '../../docked-ticket/services/ticket-edit-common-desktop.service';

@Component({
	selector: 'constraint-pending-acceptance',
	templateUrl: './constraint-pending-acceptance.component.html',
	styleUrls: ['./constraint-pending-acceptance.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	animations: [
		trigger('slideInOut', [
			transition(':enter', [
				style({ transform: 'translateY(264px)' }),
				animate('500ms cubic-bezier(0.4, 0.15, 0.15, 1.3)', style({ transform: 'translateY(0)' })),
			]),
			transition(':leave', [
				animate('500ms cubic-bezier(0.4, 0.15, 0.15, 1.3)', style({ transform: 'translateY(264px)' })),
			]),
		]),
	],
})
export class ConstraintPendingAcceptanceComponent implements OnInit, OnDestroy {
	public pendingAcceptance$: BehaviorSubject<any> = new BehaviorSubject(null);
	// Current index is the index
	public currentSelected$: BehaviorSubject<Ticket> = new BehaviorSubject(null);
	public currentIndex$: BehaviorSubject<number> = new BehaviorSubject(1);

	public shutdownSubject: Subject<boolean> = new Subject();
	public isOpen$: BehaviorSubject<boolean> = new BehaviorSubject(false);
	private _allTickets: any;
	private _totalCount = 0;
	private _pendingConstraints: any;
	private _plans: Map<string, Plan>;
	private isEnabled = true;

	constructor(
		public planState: PlanState,
		private ticketEditCommonDesktopService: TicketEditCommonDesktopService
	) {}

	public ngOnInit() {
		// We need to subscribe to the ticket list and filter
		const acceptance$ = combineLatest([
			this.planState.constraints.list$,
			this.planState.authedProjectMember$,
		]).pipe(
			map((combined) => {
				const pendingAcceptance = new Map();
				let count = 1;
				if (!combined[1]) {
					return pendingAcceptance;
				}
				// Filter the results
				combined[0].forEach((ticket, id) => {
					if (
						ticket.view.assignedProjectMemberId === combined[1].$id &&
						ticket.view.constraintState === CONSTRAINT_ACCEPTANCE_STATE.ASSIGNED &&
						!ticket.view.isPlanArchived
					) {
						pendingAcceptance.set(count, ticket);
						count++;
					}
				});
				return pendingAcceptance;
			}),
			takeUntil(this.shutdownSubject)
		);

		acceptance$.subscribe((pending) => {
			this._pendingConstraints = pending;
			// If the map receives an update, check if the current index is still valid
			// If not, set it to 1 (setIndex will handle the rest)
			const currentSelected = pending.get(this.currentIndex$.getValue());
			if (currentSelected) {
				this.currentSelected$.next(pending.get(this.currentIndex$.getValue()));
			} else {
				this.setIndex(1);
			}
			this._totalCount = pending.size;

			this.pendingAcceptance$?.next(pending);
		});

		this.planState.tickets.list$.pipe(takeUntil(this.shutdownSubject)).subscribe((tickets) => {
			this._allTickets = tickets;
		});

		this.planState.plansApi.data.list$.pipe(takeUntil(this.shutdownSubject)).subscribe((plans) => {
			this._plans = plans;
		});

		// MOC-4056 - Prevent opening of constraint pending acceptance if edit is active
		this.ticketEditCommonDesktopService
			.isTicketEditOpen()
			.pipe(takeUntil(this.shutdownSubject))
			.subscribe((ticketEditOpen) => {
				if (!ticketEditOpen) {
					this.enable();
				} else {
					this.disable();
					this.close();
				}
			});
	}

	public ngOnDestroy(): void {
		//sooo much state
		this.pendingAcceptance$.complete();
		this.pendingAcceptance$ = null;
		this.currentIndex$.complete();
		this.currentIndex$ = null;
		this.currentSelected$.complete();
		this.currentSelected$ = null;
		this.isOpen$.complete();
		this._allTickets = null;
		this._pendingConstraints = null;
		this._plans = null;

		this.shutdownSubject.next(true);
	}

	public setIndex(newIndex: number, isAccept: boolean = false) {
		if (this._totalCount - 1 <= 0) {
			newIndex = 1;
		} else if (newIndex < 1) {
			newIndex = isAccept ? this._totalCount - 1 : this._totalCount;
		} else if (newIndex > this._totalCount) {
			newIndex = 1;
		}

		this.currentIndex$.next(newIndex);
		this.currentSelected$.next(this._pendingConstraints.get(newIndex));
	}

	public cycleDown(): void {
		this.setIndex(this.currentIndex$.getValue() - 1);
	}

	public cycleUp(): void {
		this.setIndex(this.currentIndex$.getValue() + 1);
	}

	public toggle(): void {
		if (!this.isEnabled) {
			this.logDisabledMessage();
			return;
		}
		this.isOpen$.next(!this.isOpen$.getValue());
	}

	public close(): void {
		this.isOpen$.next(false);
	}

	public open(): void {
		if (!this.isEnabled) {
			this.logDisabledMessage();
			return;
		}
		this.isOpen$.next(true);
	}

	public enable(): void {
		this.isEnabled = true;
	}

	public disable(): void {
		this.isEnabled = false;
	}

	private logDisabledMessage(): void {
		window.Logging.warning(CONSTRAINT_ACCEPTANCE_PANEL_CLOSE_EDIT);
	}

	public handleCustomizeAction(): void {
		if (this.isSamePlan) {
			this.openEdit();
		} else {
			this.goToPlan();
		}
	}

	public goToPlan(): void {
		const constraintTicket = this.getCurrentSelectedConstraint();
		this.planState.scopeSoup.gotoNewPlan(
			constraintTicket.timelineTicketSplitId[0],
			constraintTicket.timelineTicketSplitId[1]
		);
	}

	public openEdit(): void {
		const constraintTicket = this.getCurrentSelectedConstraint();
		const ticket = this._allTickets.get(constraintTicket.view.id);
		this.planState.actions.changeTicketViewToEdit(ticket);
	}

	public confirmAcceptance(): void {
		let idx: number = this.currentIndex$.getValue();
		const constraintTicket = this.getCurrentSelectedConstraint();
		const ticket = this._allTickets.get(constraintTicket.view.id);
		const newData = ticket.convertConstraintToAccepted();

		this.planState.actions.updateTicketLiveData(ticket.$id, newData);
		this.planState.actions.persistTicketData(ticket);

		idx -= 1;
		this.setIndex(idx, true);
	}

	public getCurrentSelectedConstraint(): any {
		const idx: number = this.currentIndex$.getValue();
		return this._pendingConstraints.get(idx);
	}

	get otherPlanName(): string {
		if (!this._pendingConstraints || !this._pendingConstraints.size) {
			return 'N/A';
		}
		const constraintTicket = this.getCurrentSelectedConstraint();
		const plan = constraintTicket ? this._plans.get(constraintTicket.timelineTicketSplitId[0]) : null;
		return plan ? plan.view.name : 'N/A';
	}

	get customizeLabelName(): string {
		if (!this._pendingConstraints || !this._pendingConstraints.size) {
			return CONSTRAINT_ACCEPTANCE_PANEL_CUSTOMIZE_LABEL;
		}
		const constraintTicket = this.getCurrentSelectedConstraint();
		return constraintTicket && constraintTicket.timelineTicketSplitId[0] === this.planState.planId
			? CONSTRAINT_ACCEPTANCE_PANEL_CUSTOMIZE_LABEL
			: CONSTRAINT_ACCEPTANCE_PANEL_JUMP_TO_PLAN_LABEL;
	}

	get isSamePlan(): boolean {
		if (!this._pendingConstraints || !this._pendingConstraints.size) {
			return false;
		}
		const constraintTicket = this.getCurrentSelectedConstraint();
		return constraintTicket && constraintTicket.timelineTicketSplitId
			? constraintTicket.timelineTicketSplitId[0] === this.planState.planId
			: false;
	}
}
